import React from 'react';
import {useUniqueId} from '@packages/systems/spring-hooks';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type {DragEndEvent, DragMoveEvent, DragStartEvent} from '@dnd-kit/core';
import {SortableContext, arrayMove, useSortable} from '@dnd-kit/sortable';
import type {SortingStrategy} from '@dnd-kit/sortable';

import {ChevronSmallDownIcon, CloseCircleIcon} from '../../../icons';
import {HStack} from '../../Stack';
import {TagProps} from '../../Tag/Tag';
import Text from '../../Text';
import {type TextInputProps} from '../../TextInput';
import VisuallyHidden from '../../VisuallyHidden';

import {useSearchableListInput} from '../../SearchableList/useSearchableListInput';
import {
  useSearchableListContext,
  useSearchableListIndexContext,
  useSearchableListSelectionContext,
} from '../../SearchableList/useSearchableList';

import {
  AutocompleteInputControlWrapper,
  AutocompleteInputFieldWrapper,
  AutocompleteInputWrapper,
  AutocompleteTriggerButtonView,
  ClearButtonView,
  DropIndicatorView,
  InputView,
  InputWrapper,
  PlaceholderTextView,
  SelectedValueTagView,
  SelectedValueTextView,
  SortableTagWrapper,
} from './AutocompleteInput.styles';
import {
  AutocompleteProps,
  AutocompleteMultiValueColor,
  AutocompleteOption,
  AutocompleteOptions,
} from '../_types';

type AutocompleteInputProps = {
  isClearable?: AutocompleteProps<AutocompleteOptions>['isClearable'];
  isMultiValueReorderable?: AutocompleteProps<AutocompleteOptions>['isMultiValueReorderable'];
  multiValueColor?: AutocompleteProps['multiValueColor'];
  multiValueVariant?: AutocompleteProps['multiValueVariant'];
  onClear?: () => void;
  onReorder?: AutocompleteProps<AutocompleteOptions>['onReorder'];
  onReorderCaretChange?: (caret: number | null) => void;
  onReorderComplete?: AutocompleteProps<AutocompleteOptions>['onReorderComplete'];
  onInputClick?: () => void;
  onTriggerClick?: () => void;
  placeholder?: AutocompleteProps<AutocompleteOptions>['placeholder'];
  renderOptionLabel?: AutocompleteProps<AutocompleteOptions>['renderOptionLabel'];
  renderSelectedLabel?: AutocompleteProps<AutocompleteOptions>['renderSelectedLabel'];
  selectionMode: AutocompleteProps<AutocompleteOptions>['selectionMode'];
  shouldHideDropdownButton?: AutocompleteProps<AutocompleteOptions>['shouldHideDropdownButton'];
  shouldHideTagDismissButton?: AutocompleteProps<AutocompleteOptions>['shouldHideTagDismissButton'];
  tagMaxWidth?: AutocompleteProps<AutocompleteOptions>['tagMaxWidth'];
} & TextInputProps;

const tagColorVariantMap = {
  // Our Tag component does not support a 'blue' colorVariant, so we'll map it to 'primary'
  blue: 'primary',
  // Our Tag component does not support a 'gray' colorVariant, so we'll map it to 'default'
  gray: 'default',
  green: 'green',
  purple: 'purple',
  red: 'red',
  yellow: 'yellow',
  orange: 'orange',
  pink: 'pink',
} as const satisfies Record<
  AutocompleteMultiValueColor,
  TagProps['colorVariant']
>;

// No transform on siblings while a drag is in flight — only the DragOverlay
// follows the cursor. dnd-kit's built-in strategies assume a single-axis or
// stable-grid layout; a flex-wrap row of variable-width chips breaks that
// assumption at every line boundary (see noShiftSortingStrategy in
// CMSCollectionFieldGroupsList.tsx for the same precedent).
const noShiftSortingStrategy: SortingStrategy = () => null;

// Which side of `over` the dragged chip's center currently sits on. Shared by
// drag-move (for the indicator) and drag-end (for the actual reorder), so the
// two always agree: whatever gap the indicator points at is the gap that's
// dropped into.
const getDragSide = (
  active: DragMoveEvent['active'] | DragEndEvent['active'],
  over: NonNullable<DragMoveEvent['over'] | DragEndEvent['over']>
): 'left' | 'right' | null => {
  const activeRect = active.rect.current.translated;
  if (!activeRect) {
    return null;
  }
  const activeCenterX = activeRect.left + activeRect.width / 2;
  const overCenterX = over.rect.left + over.rect.width / 2;
  return activeCenterX < overCenterX ? 'left' : 'right';
};

const isSameOrder = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

/**
 * The `arrayMove` target index for a chip dropped into the gap on `side` of the
 * chip at `overIndex`. Exported for unit testing — pointer drags can't be
 * simulated meaningfully in jsdom (every rect is zero-sized), so this is the
 * only way to lock the gap-to-index conversion down.
 */
export const getDropTargetIndex = (
  fromIndex: number,
  overIndex: number,
  side: 'left' | 'right'
): number => {
  // The gap this drop targets, same as the indicator's math: left of `over` is
  // its own index, right of it is the next one.
  const gapIndex = side === 'left' ? overIndex : overIndex + 1;
  // Converting a gap index back to arrayMove's target index has to account for
  // the dragged item itself being removed first — a gap that's actually the
  // item's own current slot (immediately before or after it) must land back on
  // fromIndex, not shift by one, or hovering a neighbor (regardless of which
  // side) always counts as a real move even when it isn't one.
  return gapIndex > fromIndex ? gapIndex - 1 : gapIndex;
};

type SortableTagProps = {
  id: string;
  colorVariant: TagProps['colorVariant'];
  variant: TagProps['variant'];
  isDisabled?: boolean;
  isDismissable: boolean;
  isFocused: boolean;
  isGrabbed: boolean;
  maxWidth?: TagProps['maxWidth'];
  onBlur: () => void;
  onDismiss: () => void;
  onFocus: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLSpanElement>) => void;
  tagRef: (el: HTMLSpanElement | null) => void;
  children: React.ReactNode;
};

/**
 * A selected, focusable, reorderable tag. The whole chip is the pointer drag
 * target (dnd-kit `listeners`) and also a roving keyboard focus target: arrows
 * move focus between tags and the gaps (the input), Space picks the tag up to
 * reorder it, and Delete/Backspace removes it (see the parent's
 * `handleTagKeyDown`). `isFocused` highlights the whole chip.
 */
const SortableTag = ({
  id,
  colorVariant,
  variant,
  isDisabled,
  isDismissable,
  isFocused,
  isGrabbed,
  maxWidth,
  onBlur,
  onDismiss,
  onFocus,
  onKeyDown,
  tagRef,
  children,
}: SortableTagProps) => {
  const {listeners, setNodeRef, isDragging} = useSortable({
    id,
    disabled: isDisabled,
  });

  const setRefs = (el: HTMLSpanElement | null) => {
    setNodeRef(el);
    tagRef(el);
  };

  return (
    <SortableTagWrapper
      ref={setRefs}
      {...listeners}
      // Deliberately role-less: `option`/`listitem` both require an owning
      // container (`listbox`/`list`), and the text input is interleaved among
      // the chips, so no valid container can wrap them. The polite live region
      // the parent already emits carries the grab/move state instead — and it
      // outlives `aria-grabbed`, which ARIA 1.1 deprecated.
      data-automation-id="autocomplete-reorderable-chip"
      isGrabbed={isGrabbed}
      onBlur={onBlur}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      style={{
        cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.2 : undefined,
      }}
      tabIndex={isDisabled ? -1 : 0}
    >
      <SelectedValueTagView
        _closeButtonTabIndex={-1}
        colorVariant={colorVariant}
        density="compact"
        isDisabled={isDisabled}
        isDismissable={isDismissable}
        isFocused={isFocused && !isGrabbed}
        maxWidth={maxWidth}
        onDismiss={onDismiss}
        variant={variant}
      >
        {children}
      </SelectedValueTagView>
    </SortableTagWrapper>
  );
};

export const AutocompleteInput = React.forwardRef<
  HTMLInputElement,
  AutocompleteInputProps
>(
  (
    {
      'aria-errormessage': ariaErrorMessage,
      'aria-invalid': ariaInvalid,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      className,
      'data-automation-id': dataAutomationId,
      isClearable,
      id,
      isDisabled: isDisabledProp,
      isError,
      isMultiValueReorderable = false,
      multiValueColor: multiValueColorProp = 'gray',
      multiValueVariant: multiValueVariantProp = 'default',
      onBlur: onBlurProp,
      onClear,
      onFocus: onFocusProp,
      onInputClick,
      onKeyDown: onKeyDownProp,
      onReorder,
      onReorderCaretChange,
      onReorderComplete,
      onTriggerClick,
      placeholder,
      renderOptionLabel,
      renderSelectedLabel,
      selectionMode,
      shouldHideDropdownButton = false,
      shouldHideTagDismissButton = false,
      tagMaxWidth,
      ...props
    },
    ref
  ) => {
    const autoCompleteId = React.useId();
    const {isOpen, refs} = useSearchableListContext();
    const {
      inputRef,
      selectedOptions: committedOptions,
      toggleSelectedValue,
    } = useSearchableListSelectionContext();
    const {setActiveIndex} = useSearchableListIndexContext();

    const committedValues = React.useMemo(
      () => committedOptions.map((option) => option.value),
      [committedOptions]
    );

    // Reordering and removal both write through the consumer's onChange, which
    // is often debounced (e.g. CMS autosave) — without a local override, a
    // dropped or dismissed chip would visibly sit around at its old position
    // or stay visible until that commit lands, then jump/vanish. Applying the
    // change here immediately, and reconciling once the real value catches up,
    // makes it land right away.
    const [optimisticValues, setOptimisticValues] = React.useState<
      string[] | null
    >(null);

    React.useEffect(() => {
      if (!optimisticValues) {
        return;
      }
      const alreadyMatches =
        optimisticValues.length === committedValues.length &&
        optimisticValues.every(
          (value, index) => value === committedValues[index]
        );
      if (alreadyMatches) {
        setOptimisticValues(null);
        return;
      }
      // Safety net: if the commit we're expecting never lands as-is (e.g. the
      // consumer transforms or rejects the change), don't leave the display
      // permanently out of sync with the real value.
      const timeoutId = setTimeout(() => setOptimisticValues(null), 5000);
      return () => clearTimeout(timeoutId);
    }, [optimisticValues, committedValues]);

    const optionByValue = React.useMemo(() => {
      const map = new Map<string, AutocompleteOption>();
      committedOptions.forEach((option) => map.set(option.value, option));
      return map;
    }, [committedOptions]);

    const selectedOptions = React.useMemo(() => {
      if (!optimisticValues) {
        return committedOptions;
      }
      return optimisticValues
        .map((value) => optionByValue.get(value))
        .filter((option): option is AutocompleteOption => option != null);
    }, [optimisticValues, committedOptions, optionByValue]);

    const [activeTagIndex, setActiveTagIndex] = React.useState<number | null>(
      null
    );
    const [ariaAnnouncement, setAriaAnnouncement] = React.useState<
      string | null
    >(null);
    const [isInputFocused, setIsInputFocused] = React.useState(false);
    const [shouldHideCursor, setShouldHideCursor] = React.useState(false);
    // Reorderable mode: the cursor model mirrors a token input — the cursor is
    // either in a gap (the input, at `caretIndex` between chips) or on a tag
    // (`focusedTagId`). A grabbed tag (`grabbedId`) is being keyboard-reordered.
    // `caretIndex` of `null` means the gap at the end of the list (the default).
    const [caretIndex, setCaretIndex] = React.useState<number | null>(null);
    const [focusedTagId, setFocusedTagId] = React.useState<string | null>(null);
    const [grabbedId, setGrabbedId] = React.useState<string | null>(null);
    // Each arrow key during a grab commits its move, so cancelling has to put
    // back an order that's already been persisted — hence the snapshot taken
    // when the chip is picked up. A ref, not state: nothing renders from it.
    const grabStartOrderRef = React.useRef<string[] | null>(null);
    const tagRefs = React.useRef<Record<string, HTMLSpanElement | null>>({});

    const endTagNavigation = React.useCallback(() => {
      setActiveTagIndex(null);
      setAriaAnnouncement(null);
      setShouldHideCursor(false);
      setFocusedTagId(null);
      setGrabbedId(null);
    }, [setActiveTagIndex, setAriaAnnouncement, setShouldHideCursor]);

    const handleOnBlur = React.useCallback<
      React.FocusEventHandler<HTMLInputElement>
    >(
      (event) => {
        /**
         * Prevent the input from blurring if the relatedTarget is located within the AutocompleteInput.
         * This is in order to prevent the placeholder text from flickering.
         */
        if (
          event.relatedTarget?.closest(
            `[data-autocomplete-id="${autoCompleteId}"]`
          )
        ) {
          return;
        }

        setIsInputFocused(false);
        endTagNavigation();
        // Leaving the field resets the cursor to the end of the list, so
        // re-focusing starts fresh at the end rather than a stale gap.
        setCaretIndex(null);
        onBlurProp?.(event);
      },
      [onBlurProp, endTagNavigation, autoCompleteId]
    );

    const handleOnFocus = React.useCallback(
      (event: React.FocusEvent<HTMLInputElement, Element>) => {
        if (!isInputFocused) {
          onFocusProp?.(event);
          setIsInputFocused(true);
        }
      },
      [isInputFocused, onFocusProp]
    );

    const {
      isDisabled,
      onKeyDown,
      value: inputValue,
      ref: mergedInputRefs,
      ...autocompleteInputProps
    } = useSearchableListInput({
      isDisabled: isDisabledProp,
      onBlur: handleOnBlur,
      onFocus: handleOnFocus,
      ref,
      ...props,
    });

    const placeholderId = useUniqueId(Text, 'autocomplete-placeholder');
    const shouldRenderPlaceholder = React.useMemo(
      () => !inputValue && !selectedOptions.length,
      [inputValue, selectedOptions]
    );

    React.useEffect(() => {
      if (activeTagIndex !== null && inputValue !== '') {
        endTagNavigation();
      }
    }, [activeTagIndex, inputValue, endTagNavigation]);

    const announceActiveTag = React.useCallback(
      (index: number) => {
        const selectedOption = selectedOptions[index];
        if (!selectedOption) return;

        setAriaAnnouncement(
          `${selectedOption?.label} selected, ${index + 1} of ${
            selectedOptions.length
          }, press Backspace to remove`
        );
      },
      [selectedOptions]
    );

    const announceDeselected = React.useCallback(
      (index: number) => {
        const selectedOption = selectedOptions[index];
        if (!selectedOption) return;

        setAriaAnnouncement(`${selectedOption?.label} deselected`);
      },
      [selectedOptions]
    );

    const focusInputRef = () => {
      if (isDisabled) return;

      if (inputRef.current) {
        inputRef.current.focus();
      }

      setIsInputFocused(true);
    };

    const handleInputClick = () => {
      onInputClick?.();
      focusInputRef();
    };

    const handleOnClear = () => {
      onClear?.();
      focusInputRef();
    };

    // Only the non-reorderable render dismisses through here; the reorderable
    // chips go via removeReorderableTag. So this deliberately does not apply an
    // optimistic value — doing so would change removal behaviour for every
    // existing multi-select consumer, none of which opted into it.
    const handleTagDismiss = (value: string) => {
      toggleSelectedValue(value);
      focusInputRef();
    };

    const handleTriggerClick = () => {
      onTriggerClick?.();
      focusInputRef();
    };

    const isReorderable =
      isMultiValueReorderable && selectionMode === 'multiple';

    // dnd-kit renders its screen-reader nodes inline by default, and one of them
    // (LiveRegion) is `position: fixed` rather than hidden. Inside a scrollable
    // dialog that box sits at its static position, so the browser scrolling the
    // live region into view drags the whole dialog with it. Portalling the pair
    // to the body keeps them out of any consumer's scroll container.
    const accessibilityContainer = React.useMemo(
      () => (typeof document === 'undefined' ? undefined : document.body),
      []
    );

    const selectedValues = React.useMemo(
      () => selectedOptions.map((option) => option.value),
      [selectedOptions]
    );

    const [activeDragValue, setActiveDragValue] = React.useState<string | null>(
      null
    );
    // Which chip the pointer is currently over, and which side of it (left of
    // its horizontal center vs. right) — drives the drop indicator. Computed
    // from the live drag rects rather than array order, so it tracks the
    // actual cursor position rather than a fixed per-chip side.
    const [dropIndicator, setDropIndicator] = React.useState<{
      overId: string;
      side: 'left' | 'right';
    } | null>(null);

    const sensors = useSensors(
      useSensor(PointerSensor, {activationConstraint: {distance: 5}})
    );

    // Sets the local override so the chip lands immediately, then forwards to
    // the consumer's onReorder to actually persist it.
    const commitReorder = React.useCallback(
      (next: string[]) => {
        setOptimisticValues(next);
        onReorder?.(next);
      },
      [onReorder]
    );

    const reorderByIndex = React.useCallback(
      (fromIndex: number, toIndex: number) => {
        if (
          fromIndex === toIndex ||
          fromIndex < 0 ||
          toIndex < 0 ||
          fromIndex >= selectedValues.length ||
          toIndex >= selectedValues.length
        ) {
          return;
        }
        commitReorder(arrayMove(selectedValues, fromIndex, toIndex));
      },
      [commitReorder, selectedValues]
    );

    const handleDragStart = React.useCallback((event: DragStartEvent) => {
      // A pointer drag supersedes any in-progress keyboard grab/focus.
      setGrabbedId(null);
      grabStartOrderRef.current = null;
      setFocusedTagId(null);
      setActiveDragValue(String(event.active.id));
    }, []);

    // dnd-kit fires onDragMove on every pointer frame, but the indicator only
    // changes when the cursor crosses a chip's center. Returning the previous
    // state reference when nothing changed lets React bail out instead of
    // re-rendering every chip ~60x/sec for no visual difference.
    const handleDragMove = React.useCallback((event: DragMoveEvent) => {
      const {active, over} = event;
      const side = over ? getDragSide(active, over) : null;
      if (!over || active.id === over.id || !side) {
        setDropIndicator((prev) => (prev === null ? prev : null));
        return;
      }
      const overId = String(over.id);
      setDropIndicator((prev) =>
        prev?.overId === overId && prev.side === side ? prev : {overId, side}
      );
    }, []);

    const handleDragEnd = React.useCallback(
      (event: DragEndEvent) => {
        setActiveDragValue(null);
        setDropIndicator(null);
        const {active, over} = event;
        if (!over || active.id === over.id) {
          return;
        }
        const fromIndex = selectedValues.indexOf(String(active.id));
        const overIndex = selectedValues.indexOf(String(over.id));
        const side = getDragSide(active, over);
        if (fromIndex === -1 || overIndex === -1 || !side) {
          return;
        }
        const toIndex = getDropTargetIndex(fromIndex, overIndex, side);
        if (toIndex === fromIndex) {
          return;
        }
        reorderByIndex(fromIndex, toIndex);
        onReorderComplete?.('mouse');
      },
      [reorderByIndex, selectedValues, onReorderComplete]
    );

    const activeDragOption = React.useMemo(
      () =>
        activeDragValue
          ? selectedOptions.find((option) => option.value === activeDragValue)
          : undefined,
      [activeDragValue, selectedOptions]
    );

    // Move the cursor into a gap (the input) at index `at`, focusing the input.
    const goToGap = (at: number) => {
      setCaretIndex(at);
      setFocusedTagId(null);
      setGrabbedId(null);
      setTimeout(() => focusInputRef(), 0);
    };

    // Move the cursor onto a tag, focusing the tag element.
    const goToTag = (tagId: string) => {
      setFocusedTagId(tagId);
      setGrabbedId(null);
      setTimeout(() => tagRefs.current[tagId]?.focus(), 0);
    };

    // Remove a tag and move focus to a neighbour (or the input if none remain).
    const removeReorderableTag = (tagId: string) => {
      const idx = selectedValues.indexOf(tagId);
      const remaining = selectedValues.filter((value) => value !== tagId);
      setOptimisticValues(remaining);
      toggleSelectedValue(tagId);
      setGrabbedId(null);
      setTimeout(() => {
        if (remaining.length === 0) {
          focusInputRef();
          return;
        }
        const nextId = remaining[Math.min(idx, remaining.length - 1)];
        if (nextId) {
          tagRefs.current[nextId]?.focus();
        }
      }, 0);
    };

    // Keyboard handling for a focused tag: Space grabs/drops it for reordering,
    // arrows move it (when grabbed) or step to the adjacent gap (when not),
    // Delete/Backspace removes it.
    const handleTagKeyDown = (
      event: React.KeyboardEvent<HTMLSpanElement>,
      tagId: string
    ) => {
      const tagIdx = selectedValues.indexOf(tagId);
      if (tagIdx === -1) {
        return;
      }
      const label = selectedOptions[tagIdx]?.label;

      switch (event.key) {
        case ' ':
        case 'Spacebar':
        case 'Enter':
          event.preventDefault();
          if (grabbedId === tagId) {
            const grabStartOrder = grabStartOrderRef.current;
            setGrabbedId(null);
            grabStartOrderRef.current = null;
            setAriaAnnouncement(`${label} dropped.`);
            if (
              grabStartOrder &&
              !isSameOrder(grabStartOrder, selectedValues)
            ) {
              onReorderComplete?.('keyboard');
            }
          } else if (event.key !== 'Enter') {
            setGrabbedId(tagId);
            grabStartOrderRef.current = selectedValues;
            setAriaAnnouncement(
              `${label} grabbed. Use arrow keys to move, Space to drop, Escape to cancel.`
            );
          }
          break;
        case 'Escape':
          if (grabbedId) {
            event.preventDefault();
            const grabStartOrder = grabStartOrderRef.current;
            // Escape is a cancel, not a release — put the chip back where it
            // was picked up from. Re-focus after the row re-renders so the
            // cursor doesn't get stranded on the moved-away position.
            if (
              grabStartOrder &&
              !isSameOrder(grabStartOrder, selectedValues)
            ) {
              commitReorder(grabStartOrder);
              setTimeout(() => tagRefs.current[grabbedId]?.focus(), 0);
            }
            setGrabbedId(null);
            grabStartOrderRef.current = null;
            setAriaAnnouncement('Reorder cancelled.');
          }
          break;
        case 'ArrowLeft':
          event.preventDefault();
          if (grabbedId === tagId) {
            if (tagIdx > 0) {
              commitReorder(arrayMove(selectedValues, tagIdx, tagIdx - 1));
              setTimeout(() => tagRefs.current[tagId]?.focus(), 0);
            }
          } else {
            goToGap(tagIdx);
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (grabbedId === tagId) {
            if (tagIdx < selectedValues.length - 1) {
              commitReorder(arrayMove(selectedValues, tagIdx, tagIdx + 1));
              setTimeout(() => tagRefs.current[tagId]?.focus(), 0);
            }
          } else {
            goToGap(tagIdx + 1);
          }
          break;
        case 'Delete':
        case 'Backspace':
          event.preventDefault();
          setAriaAnnouncement(`${label} removed.`);
          removeReorderableTag(tagId);
          break;
      }
    };

    // Expose the cursor position to the parent Autocomplete so a newly selected
    // item can be inserted at the cursor in the selection's single write (see
    // `handleOnChange` in Autocomplete.tsx), rather than appended and reordered.
    React.useEffect(() => {
      onReorderCaretChange?.(caretIndex);
    }, [caretIndex, onReorderCaretChange]);

    // Reconcile the caret when the selection changes. The new item is inserted
    // at the cursor by the parent's onChange, so on an addition we just advance
    // the caret past it; on a removal we clamp the caret into range.
    const prevSelectedValuesRef = React.useRef(selectedValues);
    React.useEffect(() => {
      const prev = prevSelectedValuesRef.current;
      prevSelectedValuesRef.current = selectedValues;
      if (!isReorderable) {
        return;
      }

      if (selectedValues.length === prev.length + 1) {
        setCaretIndex(
          caretIndex == null
            ? null
            : Math.min(caretIndex + 1, selectedValues.length)
        );
      } else if (selectedValues.length < prev.length) {
        setCaretIndex(
          caretIndex == null
            ? null
            : Math.min(caretIndex, selectedValues.length)
        );
      }
    }, [selectedValues, isReorderable, caretIndex]);

    const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event);
      onKeyDownProp?.(event);

      // While a pointer drag is in progress, ignore tag navigation/removal so
      // the in-flight reorder isn't disrupted.
      if (activeDragValue !== null) {
        return;
      }

      // Reorderable multi-select uses a text caret that sits *between* chips:
      // arrows move the caret, Backspace removes the item to its left, typing
      // inserts at the caret (the input is rendered at the caret), and Space
      // "picks up" the chip left of the caret to reorder it (arrows move it;
      // Space/Esc drop it).
      if (isReorderable) {
        const len = selectedOptions.length;
        const caret = caretIndex ?? len;

        switch (event.key) {
          case 'ArrowLeft':
            // At the start of the input text, step onto the tag left of the caret.
            if (
              !inputValue &&
              event.currentTarget.selectionStart === 0 &&
              caret > 0
            ) {
              event.preventDefault();
              const prevTag = selectedOptions[caret - 1];
              if (prevTag) {
                goToTag(prevTag.value);
              }
            }
            break;
          case 'ArrowRight':
            // At the end of the input text, step onto the tag right of the caret.
            if (
              !inputValue &&
              event.currentTarget.selectionStart === inputValue.length &&
              caret < len
            ) {
              event.preventDefault();
              const nextTag = selectedOptions[caret];
              if (nextTag) {
                goToTag(nextTag.value);
              }
            }
            break;
          case 'Backspace':
            // Delete the tag immediately to the left of the caret.
            if (!inputValue && caret > 0) {
              event.preventDefault();
              const removed = selectedOptions[caret - 1];
              if (removed) {
                setOptimisticValues(
                  selectedValues.filter((v) => v !== removed.value)
                );
                toggleSelectedValue(removed.value);
                announceDeselected(caret - 1);
              }
              setCaretIndex(caret - 1 >= len - 1 ? null : caret - 1);
            }
            break;
        }
        return;
      }

      // ---- Non-reorderable multi-select: existing tag navigation model. ----
      const canNavigateTags =
        !shouldHideTagDismissButton &&
        selectionMode === 'multiple' &&
        !inputValue &&
        selectedOptions.length > 0;

      switch (event.key) {
        case 'ArrowLeft':
          if (canNavigateTags && event.currentTarget.selectionStart === 0) {
            event.preventDefault();

            if (activeTagIndex === null) {
              setActiveIndex?.(null);
              setShouldHideCursor(true);
            }

            const nextIndex =
              activeTagIndex === null
                ? selectedOptions.length - 1
                : Math.max(0, activeTagIndex - 1);
            setActiveTagIndex(nextIndex);
            announceActiveTag(nextIndex);
          }
          break;
        case 'ArrowRight':
          if (canNavigateTags && activeTagIndex !== null) {
            event.preventDefault();

            if (activeTagIndex + 1 === selectedOptions.length) {
              endTagNavigation();
            } else {
              const nextIndex =
                activeTagIndex < selectedOptions.length - 1
                  ? activeTagIndex + 1
                  : activeTagIndex;
              setActiveTagIndex(nextIndex);
              announceActiveTag(nextIndex);
            }
          }
          break;
        case 'ArrowUp':
        case 'ArrowDown':
          if (selectionMode === 'multiple' && activeTagIndex !== null) {
            event.preventDefault();
            endTagNavigation();
          }
          break;
        case 'Backspace':
          if (
            isClearable &&
            selectionMode === 'single' &&
            !inputValue &&
            selectedOptions.length
          ) {
            event.preventDefault();
            toggleSelectedValue('');
          }

          if (
            selectionMode === 'multiple' &&
            !inputValue &&
            selectedOptions.length > 0
          ) {
            event.preventDefault();

            /**
             * For multi-select, the Backspace key clears one value at a time when the input value is empty.
             *
             * If the user has navigated to a selected value tag, we remove that specific value.
             * Otherwise, we remove the last selected value.
             */
            const indexToRemove = activeTagIndex ?? selectedOptions.length - 1;
            const valueToRemove = selectedOptions[indexToRemove]?.value;
            if (valueToRemove) {
              // No optimistic value here either — this is the pre-existing
              // Backspace path shared by every multi-select consumer.
              toggleSelectedValue(valueToRemove);
            }
            setActiveTagIndex(null);
            setShouldHideCursor(false);
            announceDeselected(indexToRemove);
          }
          break;
      }
    };

    const getTagColorVariant = (option: AutocompleteOption) =>
      tagColorVariantMap[
        (option as AutocompleteOption).multiValueColor ?? multiValueColorProp
      ];

    const getTagVariant = (option: AutocompleteOption) =>
      (option as AutocompleteOption).multiValueVariant ?? multiValueVariantProp;

    /*
     * @todo Refactor Autocomplete to use renderTriggerLabel
     * @see https://webflow.atlassian.net/browse/DS-2234
     *
     * Context: renderOptionLabel is used to render the label in both the dropdown
     * menu and the input control; the 'control' context differentiates the two.
     */
    const renderTagLabel = (option: AutocompleteOption) =>
      renderSelectedLabel
        ? renderSelectedLabel(option)
        : renderOptionLabel
          ? renderOptionLabel(option, {context: 'control'})
          : option.label;

    // The text input. In reorderable mode it is rendered *between* chips at the
    // caret position; otherwise it sits after the tags. The stable key lets
    // React move (not remount) the focused input as the caret moves.
    //
    // The caret collapses to the end whenever the input itself isn't the active
    // cursor — i.e. the field is blurred, or focus is on a chip (keyboard
    // navigation / grab / reorder). Only when the cursor is genuinely in a gap
    // does the input render "inline" (sized to its content) between the chips;
    // otherwise it sits at the end so it never leaves a gap beside a chip.
    const isCursorInGap =
      isReorderable &&
      isInputFocused &&
      focusedTagId == null &&
      grabbedId == null;
    const reorderableCaret = isCursorInGap
      ? Math.min(caretIndex ?? selectedOptions.length, selectedOptions.length)
      : selectedOptions.length;
    const isInputInline = reorderableCaret < selectedOptions.length;

    const inputElement = (
      <InputWrapper
        data-value={inputValue}
        isDisabled={isDisabled}
        isInline={isInputInline}
        key="autocomplete-input"
      >
        <InputView
          aria-describedby={shouldRenderPlaceholder ? placeholderId : undefined}
          aria-errormessage={ariaErrorMessage}
          aria-invalid={ariaInvalid}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          data-1p-ignore
          data-automation-id={dataAutomationId}
          disabled={isDisabled}
          id={id}
          onKeyDown={handleOnKeyDown}
          ref={mergedInputRefs}
          shouldHideCursor={shouldHideCursor}
          value={inputValue}
          {...autocompleteInputProps}
        />
        {shouldRenderPlaceholder && (
          <PlaceholderTextView colorVariant="secondary" id={placeholderId}>
            {placeholder}
          </PlaceholderTextView>
        )}
      </InputWrapper>
    );

    // The gap index a dragged chip would land in — derived from which chip
    // the pointer is over and which side of it, so it's a single position in
    // the row rather than a marker owned by either neighbouring chip.
    const dropGapIndex = React.useMemo(() => {
      if (!dropIndicator) {
        return null;
      }
      const overIndex = selectedValues.indexOf(dropIndicator.overId);
      if (overIndex === -1) {
        return null;
      }
      return dropIndicator.side === 'left' ? overIndex : overIndex + 1;
    }, [dropIndicator, selectedValues]);
    const dropIndicatorElement = <DropIndicatorView key="drop-indicator" />;

    // Reorderable mode: interleave the input among the chips at the caret so the
    // text cursor literally sits between items for keyboard insert/delete.
    const reorderableChildren: React.ReactNode[] = [];
    selectedOptions.forEach((option, index) => {
      if (index === reorderableCaret) {
        reorderableChildren.push(inputElement);
      }
      if (index === dropGapIndex) {
        reorderableChildren.push(dropIndicatorElement);
      }
      reorderableChildren.push(
        <SortableTag
          colorVariant={getTagColorVariant(option)}
          id={option.value}
          isDisabled={isDisabled}
          isDismissable={!shouldHideTagDismissButton}
          isFocused={
            focusedTagId === option.value || grabbedId === option.value
          }
          isGrabbed={grabbedId === option.value}
          key={option.value}
          maxWidth={tagMaxWidth}
          onBlur={() => setFocusedTagId(null)}
          onDismiss={() => removeReorderableTag(option.value)}
          onFocus={() => setFocusedTagId(option.value)}
          onKeyDown={(event) => handleTagKeyDown(event, option.value)}
          tagRef={(el) => {
            tagRefs.current[option.value] = el;
          }}
          variant={getTagVariant(option)}
        >
          {renderTagLabel(option)}
        </SortableTag>
      );
    });
    // The indicator must come before the end-of-row input — that input is
    // flex-growing (fills remaining row width), so pushing the indicator
    // after it would strand the indicator at the far right of the row.
    if (dropGapIndex !== null && dropGapIndex >= selectedOptions.length) {
      reorderableChildren.push(dropIndicatorElement);
    }
    if (reorderableCaret >= selectedOptions.length) {
      reorderableChildren.push(inputElement);
    }

    return (
      <>
        <AutocompleteInputWrapper
          aria-disabled={isDisabled}
          className={className}
          data-autocomplete-id={autoCompleteId}
          isError={isError}
          ref={refs.setReference}
          tabIndex={-1}
        >
          <AutocompleteInputFieldWrapper isClearable={isClearable}>
            <VisuallyHidden>
              <span
                aria-atomic="true"
                aria-live="polite"
                aria-relevant="additions"
              >
                <span id="aria-context">{ariaAnnouncement}</span>
              </span>
            </VisuallyHidden>

            <HStack
              onClick={handleInputClick}
              shouldWrap={selectionMode === 'multiple'}
              spacing={selectionMode === 'multiple' ? 1 : 0}
              width="100%"
            >
              {selectionMode === 'single' &&
              !inputValue &&
              selectedOptions.length > 0 &&
              selectedOptions[0] ? (
                <SelectedValueTextView
                  truncateToLines={1}
                  truncationTooltipProps={{shouldNotShowTooltip: true}}
                >
                  {/*
                   * @todo Refactor Autocomplete to use renderTriggerLabel
                   *
                   * @see https://webflow.atlassian.net/browse/DS-2234
                   *
                   * Context: In LegacyAutocomplete, renderOptionLabel is used to render the label in both
                   * the dropdown menu and the text input control. The 'context' prop is used to differentiate between the two.
                   * In the future, we will update this to use renderTriggerLabel, removing the need for the `context` prop.
                   */}
                  {renderSelectedLabel
                    ? renderSelectedLabel(selectedOptions[0])
                    : renderOptionLabel
                      ? renderOptionLabel(selectedOptions[0], {
                          context: 'control',
                        })
                      : selectedOptions[0]?.label}
                </SelectedValueTextView>
              ) : null}
              {selectionMode === 'multiple' && isReorderable ? (
                /*
                 * Mounted whether or not anything is selected. Switching the
                 * provider in and out around the first chip would re-parent the
                 * text input, and React tears the old input node down and builds
                 * a new one — which drops focus and strands any open dropdown
                 * still anchored to the node that just disappeared.
                 */
                <DndContext
                  accessibility={{container: accessibilityContainer}}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                  onDragMove={handleDragMove}
                  onDragStart={handleDragStart}
                  sensors={sensors}
                >
                  <SortableContext
                    items={selectedValues}
                    strategy={noShiftSortingStrategy}
                  >
                    {/* chips interleaved with the input at the caret position */}
                    {reorderableChildren}
                  </SortableContext>
                  {/*
                   * The optimistic reorder (see commitReorder) applies the new
                   * order immediately on drop, so by the time this animates the
                   * row is already showing the chip's real final position — safe
                   * to animate toward it instead of a stale one.
                   */}
                  <DragOverlay dropAnimation={{duration: 150, easing: 'ease'}}>
                    {activeDragOption ? (
                      <SelectedValueTagView
                        colorVariant={getTagColorVariant(activeDragOption)}
                        density="compact"
                        isDismissable={false}
                        maxWidth={tagMaxWidth}
                        variant={getTagVariant(activeDragOption)}
                      >
                        {renderTagLabel(activeDragOption)}
                      </SelectedValueTagView>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              ) : null}
              {selectionMode === 'multiple' &&
                !isReorderable &&
                selectedOptions.length > 0 && (
                  <>
                    {selectedOptions.map((option, index) => (
                      <SelectedValueTagView
                        _closeButtonTabIndex={-1}
                        colorVariant={getTagColorVariant(option)}
                        density="compact"
                        isDisabled={isDisabled}
                        isDismissable={!shouldHideTagDismissButton}
                        isFocused={index === activeTagIndex}
                        key={option.value}
                        maxWidth={tagMaxWidth}
                        onDismiss={() => handleTagDismiss(option.value)}
                        variant={getTagVariant(option)}
                      >
                        {renderTagLabel(option)}
                      </SelectedValueTagView>
                    ))}
                  </>
                )}
              {/* Reorderable mode renders the input interleaved (above). */}
              {!isReorderable && inputElement}
            </HStack>
          </AutocompleteInputFieldWrapper>
          <AutocompleteInputControlWrapper>
            {isClearable && (
              <ClearButtonView
                aria-hidden
                Icon={CloseCircleIcon}
                isDisabled={isDisabled}
                label="Clear selection"
                onClick={handleOnClear}
                tabIndex={-1}
                variant="ghost"
              />
            )}
            {!shouldHideDropdownButton && (
              <AutocompleteTriggerButtonView
                data-role="autocomplete-input-trigger-button"
                Icon={ChevronSmallDownIcon}
                isDisabled={isDisabled}
                isOpen={isOpen}
                label="Toggle options"
                onClick={handleTriggerClick}
                tabIndex={-1}
                variant="ghost"
              />
            )}
          </AutocompleteInputControlWrapper>
        </AutocompleteInputWrapper>
      </>
    );
  }
);
